import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGetCurrentUserPermissionsQuery } from "../_features/permissions/api/permissionApiSlice";
import { setUserPermissions } from "../_features/auth/state/authSlice";

/**
 * Fetches the current user's permissions and dispatches them into
 * `state.auth.userPermissions`. Used to also return
 * `hasPermission`/`hasAtLeastOne`/`hasAll`/`hasWildcardAccess`/`isAdmin` -
 * all dropped here. Every one was an independent reimplementation of logic
 * that already exists in `_utils/permissions.js` (exposed properly via
 * `usePermissionCheck`), and none of them had a real caller anywhere in the
 * app - confirmed via every actual consumer of this hook, which only ever
 * reads `refetch` (or calls the hook purely for its fetch side-effect).
 * One of the dropped copies (`hasPermission`) was also a real correctness
 * bug: it checked `permission.key === permissionKey` directly, missing the
 * "permission is a plain string, not an object" case that the canonical
 * `_utils/permissions.js` version explicitly handles.
 */
export const useUserPermissions = () => {
  const dispatch = useDispatch();
  const userPermissions = useSelector((state) => state.auth.userPermissions);
  const permissionBypass = useSelector((state) => state.auth.permissionBypass);
  const token = useSelector((state) => state.auth.token);

  const {
    data: permissionsData,
    isLoading,
    error,
    refetch,
  } = useGetCurrentUserPermissionsQuery(undefined, {
    skip: !token, // Only skip if no token
    refetchOnMountOrArgChange: true,
  });

  // A fetch error (e.g. user has no role in this org) must still resolve
  // "not ready" -> "ready", otherwise every permission check stays stuck
  // treating the user as still-loading forever instead of correctly denied.
  useEffect(() => {
    if (error) {
      console.error("Error fetching user permissions:", error);
      dispatch(setUserPermissions([]));
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (permissionsData?.permissions) {
      dispatch(setUserPermissions(permissionsData.permissions));
    } else if (permissionsData) {
      // Try different possible response structures
      if (Array.isArray(permissionsData)) {
        dispatch(setUserPermissions(permissionsData));
      } else if (permissionsData.data?.permissions) {
        dispatch(setUserPermissions(permissionsData.data.permissions));
      }
    }
  }, [permissionsData, dispatch]);

  return {
    userPermissions,
    isLoading,
    error,
    refetch,
    permissionBypass,
  };
};
